import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useApproveArticleApprovalApi,
  useArticleApprovalApi,
  useCancelArticleApprovalApi,
  useRejectArticleApprovalApi
} from '@/common/api/modules/articleApproval'
import { useAuthorizedMe } from '@/common/api/modules/auth'
import {
  articleApprovalStatusBadgeType,
  articleApprovalStatusLabel
} from '@/common/app/modules/media/articleApproval'
import { articleApprovalListPath, articleDetailPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import BackButton from '@/components/view/atoms/button/BackButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import SimpleTable from '@/components/view/atoms/table/SimpleTable'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea from '@/components/view/layouts/area/InsideArea'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import ArticleApprovalScheduleEdit from '@/components/container/modules/media/article/ArticleApprovalScheduleEdit'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

const ArticleApprovalDetailPage = () => {
  const siteId = useSiteId()
  const { requestId } = useParams()
  const reqId = Number(requestId)
  const { addMessage } = useContext(ToastMessageContext)
  const { showModal, hide } = useContext(ConfirmDialogContext)
  const { operatorMe } = useAuthorizedMe()

  const { data, error, mutate } = useArticleApprovalApi(
    requestId ? { site_id: siteId, request_id: reqId } : undefined
  )

  const [comment, setComment] = useState('')
  const [scheduleEdit, setScheduleEdit] = useState(false)

  const { trigger: approve, isMutating: approving } = useApproveArticleApprovalApi({
    onSuccess: () => mutate()
  })
  const { trigger: reject, isMutating: rejecting } = useRejectArticleApprovalApi({
    onSuccess: () => mutate()
  })
  const voting = approving || rejecting
  const { trigger: cancelRequest } = useCancelArticleApprovalApi({
    onSuccess: () => {
      mutate()
      hide()
      addMessage({ type: 'success', text: '承認リクエストをキャンセルしました' })
    }
  })

  const handleVote = (v: 'approved' | 'rejected') => {
    const trigger = v === 'approved' ? approve : reject
    trigger({ site_id: siteId, request_id: reqId, comment: comment.trim() || null })
  }

  const handleCancel = () =>
    showModal({
      noText: 'キャンセル',
      yesText: 'リクエストを取消',
      message: 'この承認リクエストをキャンセルしますか？',
      onClickYes: async () => {
        await cancelRequest({ site_id: siteId, request_id: reqId })
      }
    })

  const canEditSchedule = data?.status === 'pending' || data?.status === 'upload_ready'

  return (
    <DetailPageLoader error={error} data={data}>
      {data && (
        <Main type={MainLayoutType.SMALL}>
          <BackButton link={articleApprovalListPath(siteId)} />
          <PageTitle
            title='承認リクエスト詳細'
            badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
            behindNode={
              <Badge badgeType={articleApprovalStatusBadgeType(data.status)}>
                {articleApprovalStatusLabel(data.status)}
              </Badge>
            }
            description='記事バージョンに対する承認リクエストの状況と承認履歴が確認できます。'
          />

          <Content>
            {/* 記事情報 + ステータス */}
            <Area>
              <Heading>リクエスト詳細</Heading>
              <AreaContent>
                <FormRow align='center'>
                  <FormLabel>リクエストID</FormLabel>
                  <Text>{data.id}</Text>
                </FormRow>
                <FormRow align='center'>
                  <FormLabel>対象記事</FormLabel>
                  {data.article_proposal_id ? (
                    <TextButton link={articleDetailPath(siteId, data.article_proposal_id)} newTab>
                      {data.title}
                    </TextButton>
                  ) : (
                    <Text>{data.title || '-'}</Text>
                  )}
                </FormRow>
                <FormRow align='center'>
                  <FormLabel>Version</FormLabel>
                  <Text>v{data.version_number}</Text>
                </FormRow>
                <FormRow align='center'>
                  <FormLabel>依頼者</FormLabel>
                  <Text>
                    {data.requested_by_name || '-'}
                    {data.requested_at ? `（${dateTimeStr(data.requested_at)}）` : ''}
                  </Text>
                  {data.status === 'pending' && (
                    <Button color={ButtonColor.SECONDARY} onClick={handleCancel}>
                      承認依頼キャンセル
                    </Button>
                  )}
                </FormRow>
                <FormRow align='center'>
                  <FormLabel>アップロード予定日時</FormLabel>
                  <Text>
                    {data.scheduled_upload_at ? dateTimeStr(data.scheduled_upload_at) : '未設定'}
                  </Text>
                  {canEditSchedule && (
                    <DisclosureWrapper
                      off={!scheduleEdit}
                      disclosureNode={
                        <ArticleApprovalScheduleEdit
                          requestId={reqId}
                          siteId={siteId}
                          scheduledUploadAt={data.scheduled_upload_at}
                          onChange={() => {
                            mutate()
                            setScheduleEdit(false)
                          }}
                          onClickCancel={() => setScheduleEdit(false)}
                        />
                      }
                    >
                      <Button
                        color={ButtonColor.SECONDARY}
                        onClick={() => {
                          setScheduleEdit(true)
                        }}
                      >
                        変更
                      </Button>
                    </DisclosureWrapper>
                  )}
                </FormRow>
                <FormRow align='center'>
                  <FormLabel>リクエストステータス</FormLabel>
                  <Badge badgeType={articleApprovalStatusBadgeType(data.status)} size={10}>
                    {articleApprovalStatusLabel(data.status)}
                  </Badge>{' '}
                </FormRow>
              </AreaContent>
            </Area>

            {/* 承認状況（承認可能なオペレーター一覧。単一承認方式：誰か1人が承認・差戻すれば確定） */}
            <Area>
              <Heading>承認状況</Heading>
              <AreaContent>
                <SimpleTable noMargin>
                  <TableRow head>
                    <TableHeadItem width={128} type='thin'>
                      <Text size={12}>承認者</Text>
                    </TableHeadItem>
                    <TableHeadItem width={88} type='thin'>
                      <Text size={12}>結果</Text>
                    </TableHeadItem>
                    <TableHeadItem type='thin'>
                      <Text size={12}>コメント</Text>
                    </TableHeadItem>
                    <TableHeadItem width={160} type='thin'>
                      <Text size={12}>日時</Text>
                    </TableHeadItem>
                  </TableRow>
                  <>
                    {data.can_approve ? (
                      <>
                        {data.approvers.map((approver) => {
                          const editable =
                            !approver.result && approver.operator_id === operatorMe?.id
                          return (
                            <TableRow key={approver.operator_id}>
                              <TableRowItem width={128}>
                                <Text size={14}>{approver.name}</Text>
                              </TableRowItem>
                              <TableRowItem width={88}>
                                {approver.result === 'approved' ? (
                                  <Text size={14} color={TextColor.SUCCESS}>
                                    承認
                                  </Text>
                                ) : approver.result === 'rejected' ? (
                                  <Text size={14} color={TextColor.CRITICAL}>
                                    差戻
                                  </Text>
                                ) : (
                                  <Text size={14} color={TextColor.SECONDARY}>
                                    -
                                  </Text>
                                )}
                              </TableRowItem>
                              <TableRowItem>
                                {editable ? (
                                  <TextField
                                    size={TextBoxSize.FULL}
                                    value={comment}
                                    placeholder='コメントを入力...'
                                    onChange={(e) => setComment(e.target.value)}
                                  />
                                ) : (
                                  <Text size={12} color={TextColor.SECONDARY}>
                                    {approver.comment || '-'}
                                  </Text>
                                )}
                              </TableRowItem>
                              <TableRowItem width={160}>
                                <Text size={14} color={TextColor.SECONDARY}>
                                  {approver.acted_at ? dateTimeStr(approver.acted_at) : '-'}
                                </Text>
                              </TableRowItem>
                            </TableRow>
                          )
                        })}
                      </>
                    ) : (
                      <>
                        <TableRow>
                          <TableRowItem width={128}>
                            <Text size={14}>{data.approved_by_name ?? data.requested_by_name}</Text>
                          </TableRowItem>
                          <TableRowItem width={88}>
                            {data.approved_by_name ? (
                              <Badge badgeType={BadgeType.SUCCESS}>承認</Badge>
                            ) : data.rejected_by_name ? (
                              <Badge badgeType={BadgeType.CRITICAL}>差し戻し</Badge>
                            ) : (
                              <Text size={14} color={TextColor.SECONDARY}>
                                -
                              </Text>
                            )}
                          </TableRowItem>
                          <TableRowItem>
                            <Text size={12} color={TextColor.SECONDARY}>
                              {data.approval_comment || data.reject_comment || '-'}
                            </Text>
                          </TableRowItem>
                          <TableRowItem width={160}>
                            <Text size={14} color={TextColor.SECONDARY}>
                              {data.approved_at
                                ? dateTimeStr(data.approved_at)
                                : data.rejected_at
                                  ? dateTimeStr(data.rejected_at)
                                  : '-'}
                            </Text>
                          </TableRowItem>
                        </TableRow>
                      </>
                    )}
                  </>
                </SimpleTable>

                {/* 承認・差戻ボタン（承認権限を持つオペレーターなら依頼者本人でも実行可） */}
                {data.can_approve && (
                  <InsideArea>
                    <div className='flex flex-col gap-3 p-3'>
                      {!data.approvers.some(
                        (approver) => approver.operator_id === operatorMe?.id
                      ) && (
                        <div className='flex flex-col gap-1'>
                          <Text size={12} color={TextColor.SECONDARY}>
                            コメント（任意）
                          </Text>
                          <TextField
                            size={TextBoxSize.FULL}
                            value={comment}
                            placeholder='コメントを入力...'
                            onChange={(e) => setComment(e.target.value)}
                          />
                        </div>
                      )}
                      <ItemAlign gapX={8} justify='center'>
                        <Button
                          color={ButtonColor.SECONDARY}
                          size='large'
                          loading={voting}
                          onClick={() => handleVote('rejected')}
                        >
                          差戻
                        </Button>
                        <Button
                          color={ButtonColor.PRIMARY_BRAND}
                          size='large'
                          loading={voting}
                          onClick={() => handleVote('approved')}
                        >
                          承認する
                        </Button>
                      </ItemAlign>
                    </div>
                  </InsideArea>
                )}
              </AreaContent>
            </Area>
          </Content>
        </Main>
      )}
    </DetailPageLoader>
  )
}

export default ArticleApprovalDetailPage
